/**
 * Referral Hooks
 *
 * Custom React hooks for the referral system including code management,
 * URL parameter handling, and referral application.
 *
 * Aligned with backend API at serverless/api/routes/referral.py
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { referralApi } from '../services/referralApi';
import { useAuth } from './useAuth';
import { STORAGE_KEYS } from '../constants/storage';
import type { ReferralCode, ReferralStats, ApplyReferralResponse } from '../services/referralApi';
import { handleError } from '../utils/errorHandler';

// URL parameter name for referral codes
const REFERRAL_PARAM = 'ref';

// ============================================================================
// URL PARAMETER HOOK
// ============================================================================

interface UseReferralCodeFromUrlReturn {
  /** The referral code from URL or localStorage */
  referralCode: string | null;
  /** Clear the stored referral code */
  clearReferralCode: () => void;
}

/**
 * Hook to extract and store referral code from URL
 *
 * Captures the ?ref=CODE parameter from the URL, stores it in localStorage,
 * and cleans the URL for better UX. The stored code is used during signup.
 */
export function useReferralCodeFromUrl(): UseReferralCodeFromUrlReturn {
  const [referralCode, setReferralCode] = useState<string | null>(null);

  useEffect(() => {
    // Check URL for referral code
    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get(REFERRAL_PARAM);

    if (codeFromUrl) {
      // Store in localStorage for use after signup
      const normalizedCode = codeFromUrl.toUpperCase().trim();
      localStorage.setItem(STORAGE_KEYS.REFERRAL_CODE, normalizedCode);
      setReferralCode(normalizedCode);

      // Clean URL (remove ref param)
      urlParams.delete(REFERRAL_PARAM);
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } else {
      // Check localStorage for existing code
      const stored = localStorage.getItem(STORAGE_KEYS.REFERRAL_CODE);
      if (stored) {
        setReferralCode(stored);
      }
    }
  }, []);

  const clearReferralCode = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);
    setReferralCode(null);
  }, []);

  return { referralCode, clearReferralCode };
}

// ============================================================================
// REFERRAL CODE HOOK
// ============================================================================

interface UseReferralCodeReturn {
  /** User's referral data */
  data: ReferralCode | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Refetch the referral code */
  refetch: () => Promise<void>;
}

/**
 * Hook to get the user's referral code
 *
 * Fetches the user's unique referral code from the API.
 * Creates a code if the user doesn't have one.
 */
export function useReferralCode(): UseReferralCodeReturn {
  const { user } = useAuth();
  const [data, setData] = useState<ReferralCode | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCode = useCallback(async () => {
    if (!user) {
      setData(null);
      return;
    }

    setIsLoading(true);
    try {
      const result = await referralApi.getCode();
      setData(result);
      setError(null);
    } catch (err) {
      setError(handleError(err, 'Referral', 'Failed to fetch referral code'));
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCode();
  }, [fetchCode]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchCode,
  };
}

// ============================================================================
// REFERRAL STATS HOOK
// ============================================================================

interface UseReferralStatsReturn {
  /** User's detailed referral stats */
  stats: ReferralStats | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Refetch the stats */
  refetch: () => Promise<void>;
}

/**
 * Hook to get detailed referral statistics
 *
 * Includes recent referrals and remaining referral limit.
 */
export function useReferralStats(): UseReferralStatsReturn {
  const { user } = useAuth();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!user) {
      setStats(null);
      return;
    }

    setIsLoading(true);
    try {
      const result = await referralApi.getStats();
      setStats(result);
      setError(null);
    } catch (err) {
      setError(handleError(err, 'Referral', 'Failed to fetch referral stats'));
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    isLoading,
    error,
    refetch: fetchStats,
  };
}

// ============================================================================
// APPLY REFERRAL HOOK
// ============================================================================

interface UseApplyReferralReturn {
  /** Apply a referral code */
  apply: (code: string) => Promise<ApplyReferralResponse | null>;
  /** Applying state */
  isApplying: boolean;
  /** Last successful application result */
  result: ApplyReferralResponse | null;
  /** Error message */
  error: string | null;
  /** Error code (for specific error handling) */
  errorCode: string | null;
  /** Clear the error */
  clearError: () => void;
}

/**
 * Hook to apply a referral code
 *
 * Applies a referral code for the current user, granting tokens
 * to both the applicant and the code owner.
 */
export function useApplyReferral(
  onSuccess?: (result: ApplyReferralResponse) => void
): UseApplyReferralReturn {
  const [isApplying, setIsApplying] = useState(false);
  const [result, setResult] = useState<ApplyReferralResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const apply = useCallback(
    async (code: string): Promise<ApplyReferralResponse | null> => {
      setIsApplying(true);
      setError(null);
      setErrorCode(null);

      try {
        const response = await referralApi.apply(code);
        setResult(response);
        onSuccess?.(response);
        return response;
      } catch (err) {
        // Parse error code from API response if available
        const message = handleError(err, 'Referral', 'Failed to apply referral code');
        setError(message);

        // Extract error code from message if present
        if (message.includes('INVALID_REFERRAL_CODE')) {
          setErrorCode('INVALID_REFERRAL_CODE');
        } else if (message.includes('SELF_REFERRAL')) {
          setErrorCode('SELF_REFERRAL');
        } else if (message.includes('ALREADY_REFERRED')) {
          setErrorCode('ALREADY_REFERRED');
        } else if (message.includes('REFERRAL_LIMIT_REACHED')) {
          setErrorCode('REFERRAL_LIMIT_REACHED');
        }

        return null;
      } finally {
        setIsApplying(false);
      }
    },
    [onSuccess]
  );

  const clearError = useCallback(() => {
    setError(null);
    setErrorCode(null);
  }, []);

  return {
    apply,
    isApplying,
    result,
    error,
    errorCode,
    clearError,
  };
}

// ============================================================================
// AUTO-APPLY REFERRAL HOOK
// ============================================================================

interface UseAutoApplyReferralReturn {
  /** Whether auto-apply has been attempted */
  hasAttempted: boolean;
  /** Whether auto-apply was successful */
  wasSuccessful: boolean;
  /** Result of the application */
  result: ApplyReferralResponse | null;
  /** Error if application failed */
  error: string | null;
}

/**
 * Hook to automatically apply stored referral code after login
 *
 * Should be used in the authentication flow to auto-apply any
 * referral code stored from the URL.
 */
export function useAutoApplyReferral(): UseAutoApplyReferralReturn {
  const { user } = useAuth();
  const { referralCode, clearReferralCode } = useReferralCodeFromUrl();
  const [hasAttempted, setHasAttempted] = useState(false);
  const [wasSuccessful, setWasSuccessful] = useState(false);
  const [result, setResult] = useState<ApplyReferralResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const attemptedRef = useRef(false);

  useEffect(() => {
    // Only attempt once per session
    if (attemptedRef.current) return;
    if (!user || !referralCode) return;

    attemptedRef.current = true;
    setHasAttempted(true);

    const applyCode = async () => {
      try {
        const response = await referralApi.apply(referralCode);
        setResult(response);
        setWasSuccessful(true);
        clearReferralCode();
      } catch (err) {
        setError(handleError(err, 'Referral', 'Failed to apply referral code'));
        // Clear the code even on error to prevent retrying
        clearReferralCode();
      }
    };

    applyCode();
  }, [user, referralCode, clearReferralCode]);

  return {
    hasAttempted,
    wasSuccessful,
    result,
    error,
  };
}

// ============================================================================
// COMBINED REFERRAL HOOK
// ============================================================================

interface UseReferralReturn {
  /** User's referral code data */
  code: UseReferralCodeReturn;
  /** Detailed referral statistics */
  stats: UseReferralStatsReturn;
  /** Apply a referral code */
  apply: UseApplyReferralReturn;
  /** Referral code from URL/storage */
  storedCode: UseReferralCodeFromUrlReturn;
  /** Refetch all referral data */
  refetchAll: () => Promise<void>;
}

/**
 * Combined hook for all referral functionality
 *
 * Provides a unified interface for the referral system.
 */
export function useReferral(): UseReferralReturn {
  const code = useReferralCode();
  const stats = useReferralStats();
  const storedCode = useReferralCodeFromUrl();
  const applyHook = useApplyReferral(() => {
    code.refetch();
    stats.refetch();
  });

  const refetchAll = useCallback(async () => {
    await Promise.all([code.refetch(), stats.refetch()]);
  }, [code, stats]);

  return {
    code,
    stats,
    apply: applyHook,
    storedCode,
    refetchAll,
  };
}
